import { Link } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import { HeroReveal, InteractiveDots, Pressable, ScrollReveal, StaggerContainer, StaggerItem, TextReveal } from '@/components/motion'
import { StatsRow, StickyFeatures } from '@/components/landing/service'
import { ListingCard } from '@/components/landing/listing'
import type { IndustryData } from '@/data/industries'
import { IndustryCtaBand } from './IndustryCtaBand'
import { TemplateCover, joinList, lowerFirst, manualTime, sentences, shortTime, templateExcerpt } from './listing'
import { WorkflowDiagram } from './WorkflowDiagram'

/**
 * Stats-hero layout (Finance; strategy-insights reference in purple):
 * full-bleed purple hero with the name in a pink box and the stats inside,
 * the workflows pinned as diagrams, a compact four-column template grid,
 * the ROI metrics as large before/after rows and a black CTA band.
 */
export function StatsHeroLayout({ industry }: { industry: IndustryData }) {
  const lines = sentences(industry.headline)

  return (
    <>
      {/* 1. Hero: purple band, pink name box, H1, lede, stats */}
      <section className="relative overflow-hidden bg-[var(--color-landing-secondary)] text-white">
        <InteractiveDots />
        <div className="landing-container relative z-10 pb-16 pt-16 md:pb-20 md:pt-24 lg:pb-24 lg:pt-28">
          <div className="lg:mx-auto lg:w-[86%]">
            <HeroReveal delay={0}>
              <p className="font-[family-name:var(--font-landing-display)] text-[clamp(1.4rem,2.6vw,2.75rem)] uppercase leading-[1.3]">
                <span className="inline-block -rotate-1 bg-[#ff00f2] px-[0.35em] py-[0.15em] text-white">{industry.name}.</span>
              </p>
            </HeroReveal>
            <HeroReveal delay={0.1}>
              <h1 className="mt-6 font-[family-name:var(--font-landing-display)] text-[clamp(1.6rem,4.1vw,4.75rem)] uppercase leading-[1.3] tracking-[-0.02em] text-white md:mt-8">
                {lines.map((line, i) => (
                  <span key={line} className="block">
                    {line}
                    {i < lines.length - 1 ? ' ' : ''}
                  </span>
                ))}
              </h1>
            </HeroReveal>
            <HeroReveal delay={0.2}>
              <p className="landing-text-body-lg mt-8 max-w-[58ch] text-pretty text-white/85 md:mt-10 md:text-xl">
                {industry.subheadline}
              </p>
            </HeroReveal>
            <HeroReveal delay={0.3} className="mt-10 flex flex-wrap gap-4">
              <Pressable className="inline-block">
                <Link
                  to="/contact"
                  className="landing-cta landing-cta--solid landing-cta--lg landing-cta--on-color landing-btn-sweep border-[#ff00f2] bg-[#ff00f2] text-white hover:text-[var(--color-landing-primary)] dark:border-[#ff00f2] dark:bg-[#ff00f2] dark:text-white"
                >
                  Book a demo
                  <ArrowRight aria-hidden className="h-5 w-5" />
                </Link>
              </Pressable>
              <a href="#workflows" className="landing-cta landing-cta--outline landing-cta--lg landing-cta--on-color landing-btn-sweep">
                See the workflows
              </a>
            </HeroReveal>

            <StatsRow stats={industry.heroStats} immediate className="mt-16 md:mt-24" />
          </div>
        </div>
      </section>

      {/* 2. Workflows pinned: trigger → steps → output as a diagram */}
      <section id="workflows" aria-labelledby="workflows-heading" className="landing-section scroll-mt-20">
        <div className="landing-container mb-12 lg:mb-0">
          <TextReveal as="h2" className="landing-text-display-xl max-w-[20ch] text-[var(--color-landing-gray-900)] dark:text-white">
            <span id="workflows-heading">
              Four workflows, <span className="landing-highlight landing-highlight--secondary">end to end.</span>
            </span>
          </TextReveal>
        </div>
        <StickyFeatures
          accent="purple"
          steps={industry.workflows.map((workflow) => ({
            title: workflow.name,
            description: `Starts when: ${lowerFirst(workflow.trigger)}. Ends with: ${lowerFirst(workflow.output)}.`,
            extra: (
              <p className="flex flex-wrap items-center gap-3 text-sm font-bold uppercase tracking-[0.04em] text-[var(--color-landing-gray-900)] dark:text-white">
                <span className="bg-[#ff00f2] px-2 py-1 text-[#0a0a0a]">{shortTime(workflow.timeSaved)}</span>
                {manualTime(workflow.timeSaved) && (
                  <span className="text-[var(--color-landing-gray-500)] dark:text-[var(--color-landing-gray-400)]">
                    vs. {manualTime(workflow.timeSaved)}
                  </span>
                )}
              </p>
            ),
            visual: (active: boolean) => <WorkflowDiagram workflow={workflow} agents={industry.aiAgents} active={active} />,
          }))}
        />
      </section>

      {/* 3. Templates: compact four-column grid, no filters */}
      <section id="templates" className="landing-section scroll-mt-20 border-t border-[var(--color-landing-gray-200)] dark:border-[var(--color-landing-gray-800)]">
        <div className="landing-container">
          <div className="grid gap-8 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)] lg:items-end lg:gap-20">
            <TextReveal as="h2" className="landing-text-display-xl text-[var(--color-landing-gray-900)] dark:text-white">
              The {industry.shortName.toLowerCase()} template set
            </TextReveal>
            <ScrollReveal direction="none" delay={0.15}>
              <p className="landing-text-body-lg max-w-[48ch] border-l border-[var(--color-landing-gray-300)] pl-5 text-[var(--color-landing-gray-600)] dark:border-[var(--color-landing-gray-700)] dark:text-[var(--color-landing-gray-400)]">
                {industry.templates.length} templates for documents that answer to {joinList(industry.complianceRequirements)}.
              </p>
            </ScrollReveal>
          </div>
          <StaggerContainer amount={0.05} className="mt-14 grid gap-x-5 gap-y-10 sm:grid-cols-2 lg:grid-cols-4">
            {industry.templates.map((template, index) => (
              <StaggerItem key={template.name}>
                <ListingCard
                  href="/platform/templates"
                  cover={<TemplateCover template={template} index={index} />}
                  title={template.name}
                  size="h6"
                  tags={[{ label: `${template.type} template`, tone: 'purple' }]}
                  tagsPosition="above"
                  excerpt={templateExcerpt(template)}
                  excerptLines={2}
                />
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* 4. ROI: large before/after rows */}
      <section aria-labelledby="roi-heading" className="landing-section bg-[var(--color-landing-gray-50)] dark:bg-[#12121a]">
        <div className="landing-container">
          <TextReveal as="h2" className="landing-text-display-xl text-[var(--color-landing-gray-900)] dark:text-white">
            <span id="roi-heading">
              Before, and <span className="landing-highlight">after.</span>
            </span>
          </TextReveal>
          <ul className="mt-14 border-t border-[var(--color-landing-gray-900)] dark:border-white">
            {industry.roiMetrics.map((metric) => (
              <li key={metric.metric}>
                <ScrollReveal
                  direction="none"
                  amount={0.4}
                  className="grid items-baseline gap-x-8 gap-y-3 border-b border-[var(--color-landing-gray-300)] py-8 dark:border-[var(--color-landing-gray-700)] md:py-10 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1fr)_auto_minmax(0,1.15fr)_minmax(0,0.55fr)]"
                >
                  <h3 className="font-[family-name:var(--font-landing-subdisplay)] text-lg font-bold uppercase tracking-[0.02em] text-[var(--color-landing-gray-900)] dark:text-white md:text-xl">
                    {metric.metric}
                  </h3>
                  <p className="flex flex-wrap items-baseline gap-x-5 gap-y-1 lg:contents">
                    <span className="font-[family-name:var(--font-landing-display)] text-[clamp(1.25rem,2.4vw,2.6rem)] uppercase leading-[1.3] tracking-[-0.02em] text-[var(--color-landing-gray-900)]/45 line-through decoration-2 dark:text-white/45">
                      <span className="sr-only">Before: </span>
                      {metric.before}
                    </span>
                    <ArrowRight aria-hidden className="h-6 w-6 shrink-0 self-center text-[var(--color-landing-secondary)] md:h-8 md:w-8" />
                    <span className="font-[family-name:var(--font-landing-display)] text-[clamp(1.6rem,3.4vw,3.75rem)] uppercase leading-[1.3] tracking-[-0.02em] text-[var(--color-landing-gray-900)] dark:text-white">
                      <span className="sr-only">After: </span>
                      {metric.after}
                    </span>
                  </p>
                  <span className="inline-block self-center justify-self-start bg-[var(--color-landing-secondary)] px-2.5 py-1.5 text-sm font-bold uppercase tracking-[0.04em] text-white lg:justify-self-end">
                    {metric.improvement}
                  </span>
                </ScrollReveal>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* 5. CTA band: black */}
      <IndustryCtaBand industry={industry} tone="black" />
    </>
  )
}
